import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCityContext } from '../../hooks/useCityContext';
import { FUNDING_TYPES, City } from '../../config/cities';
import { track } from '../../utils/tracking';
import { isValidCitySlug } from '../../utils/routeGenerator';
import { getCityFAQs } from '../../utils/faqContent';
import { generateCityPageSchema } from '../../utils/schema';
import { getArticlesByCity } from '../../utils/articleData';

// City-specific economic data
const getCityEconomicData = (city: City) => {
  const economicData: Record<string, {
    keyIndustries: string[];
    economicHighlights: string[];
    businessClimate: string;
    avgLoanAmount: string;
    topFundingPurpose: string;
    businessGrowthRate: string;
  }> = {
    'new-york': {
      keyIndustries: ['Financial Services', 'Technology', 'Healthcare', 'Media & Entertainment', 'Real Estate', 'Professional Services'],
      economicHighlights: [
        'Largest city economy in the United States with GDP over $1.5 trillion',
        'Home to Wall Street and major financial institutions',
        'Leading hub for startups and venture capital investment',
        'Diverse immigrant entrepreneur community driving small business growth'
      ],
      businessClimate: 'New York offers unparalleled access to capital, talent, and markets. While operating costs are higher, the density of opportunities and networking potential make it ideal for ambitious businesses seeking rapid growth.',
      avgLoanAmount: '$175,000',
      topFundingPurpose: 'Working Capital & Expansion',
      businessGrowthRate: '4.2%'
    },
    'miami': {
      keyIndustries: ['Tourism & Hospitality', 'International Trade', 'Real Estate', 'Healthcare', 'Finance', 'Technology'],
      economicHighlights: [
        'Gateway to Latin America with strong international trade connections',
        'Fastest-growing tech hub in the Southeast',
        'No state income tax benefits for business owners',
        'Year-round tourism drives consistent revenue for service businesses'
      ],
      businessClimate: 'Miami\'s strategic location and tax advantages make it attractive for businesses with international operations. The growing tech scene and influx of remote workers have diversified the economy beyond tourism.',
      avgLoanAmount: '$125,000',
      topFundingPurpose: 'Real Estate & Inventory',
      businessGrowthRate: '5.8%'
    },
    'dallas': {
      keyIndustries: ['Technology', 'Healthcare', 'Financial Services', 'Telecommunications', 'Defense', 'Energy'],
      economicHighlights: [
        'One of the fastest-growing metropolitan economies in the US',
        'Major corporate relocation destination with 24 Fortune 500 headquarters',
        'No state income tax and business-friendly regulations',
        'Strong talent pipeline from major universities'
      ],
      businessClimate: 'Dallas offers a perfect blend of low operating costs and access to major markets. The pro-business environment and diverse economy make it ideal for companies looking to scale efficiently.',
      avgLoanAmount: '$150,000',
      topFundingPurpose: 'Equipment & Expansion',
      businessGrowthRate: '4.9%'
    },
    'houston': {
      keyIndustries: ['Energy & Oil/Gas', 'Healthcare & Medical', 'Aerospace', 'Manufacturing', 'Shipping & Logistics', 'Technology'],
      economicHighlights: [
        'Energy capital of the world with major oil and gas headquarters',
        'Texas Medical Center is the largest medical complex globally',
        'Port of Houston is one of the busiest in the nation',
        'Diverse economy transitioning into renewable energy and tech'
      ],
      businessClimate: 'Houston\'s economy is diversifying beyond energy into healthcare, technology, and manufacturing. The low cost of living and no state income tax attract entrepreneurs and skilled workers alike.',
      avgLoanAmount: '$165,000',
      topFundingPurpose: 'Equipment & Working Capital',
      businessGrowthRate: '3.8%'
    },
    'los-angeles': {
      keyIndustries: ['Entertainment & Media', 'Technology', 'International Trade', 'Fashion & Apparel', 'Aerospace', 'Tourism'],
      economicHighlights: [
        'Entertainment capital with major studios and streaming companies',
        'Largest manufacturing center in the United States',
        'Port of LA handles 40% of US container imports',
        'Thriving startup ecosystem in Silicon Beach'
      ],
      businessClimate: 'Los Angeles offers access to creative talent, diverse markets, and international trade routes. While costs are higher, the market size and industry connections provide significant growth opportunities.',
      avgLoanAmount: '$185,000',
      topFundingPurpose: 'Working Capital & Real Estate',
      businessGrowthRate: '3.5%'
    },
    'chicago': {
      keyIndustries: ['Financial Services', 'Manufacturing', 'Transportation & Logistics', 'Healthcare', 'Food Processing', 'Technology'],
      economicHighlights: [
        'Third-largest city economy with diverse industrial base',
        'Major transportation hub with O\'Hare Airport and rail networks',
        'Home to CME Group and major commodities exchanges',
        'Strong manufacturing heritage with modern tech integration'
      ],
      businessClimate: 'Chicago combines Midwest affordability with big-city resources. Its central location and transportation infrastructure make it ideal for businesses with national distribution needs.',
      avgLoanAmount: '$140,000',
      topFundingPurpose: 'Equipment & Inventory',
      businessGrowthRate: '2.9%'
    },
    'atlanta': {
      keyIndustries: ['Logistics & Transportation', 'Technology', 'Film & Entertainment', 'Healthcare', 'Financial Services', 'Food & Beverage'],
      economicHighlights: [
        'Busiest airport in the world drives business connectivity',
        'Major film and TV production hub ("Hollywood of the South")',
        'Home to 18 Fortune 500 company headquarters',
        'Fast-growing fintech and cybersecurity sectors'
      ],
      businessClimate: 'Atlanta offers Southern hospitality with big-city opportunities. Lower costs than coastal cities combined with excellent connectivity make it a top choice for growing businesses.',
      avgLoanAmount: '$130,000',
      topFundingPurpose: 'Expansion & Working Capital',
      businessGrowthRate: '4.5%'
    },
    'phoenix': {
      keyIndustries: ['Technology', 'Healthcare', 'Real Estate', 'Manufacturing', 'Financial Services', 'Aerospace'],
      economicHighlights: [
        'One of the fastest-growing cities in America',
        'Major semiconductor manufacturing hub',
        'Lower cost of living attracting remote workers and businesses',
        'Strong construction and real estate development sector'
      ],
      businessClimate: 'Phoenix\'s rapid growth creates abundant opportunities for businesses serving expanding populations. The business-friendly environment and lower costs make it attractive for startups and relocations.',
      avgLoanAmount: '$115,000',
      topFundingPurpose: 'Real Estate & Equipment',
      businessGrowthRate: '6.2%'
    },
    'las-vegas': {
      keyIndustries: ['Hospitality & Gaming', 'Entertainment', 'Conventions & Events', 'Construction', 'Healthcare', 'Technology'],
      economicHighlights: [
        'World\'s premier destination for conventions and trade shows',
        'No state income tax and business-friendly regulations',
        'Diversifying economy beyond gaming into tech and healthcare',
        'Major sports and entertainment venue developments'
      ],
      businessClimate: 'Las Vegas is evolving beyond gaming into a diversified economy. The convention industry provides B2B opportunities, while population growth drives demand for services.',
      avgLoanAmount: '$110,000',
      topFundingPurpose: 'Working Capital & Equipment',
      businessGrowthRate: '5.1%'
    },
    'tampa': {
      keyIndustries: ['Healthcare', 'Financial Services', 'Technology', 'Tourism', 'Defense', 'Marine & Shipping'],
      economicHighlights: [
        'Fast-growing tech hub with major cybersecurity presence',
        'No state income tax benefits business owners',
        'Port Tampa Bay drives international trade',
        'Strong healthcare and life sciences sector'
      ],
      businessClimate: 'Tampa offers Florida\'s tax advantages with a more affordable cost of living than Miami. The growing tech scene and quality of life attract talent and businesses alike.',
      avgLoanAmount: '$105,000',
      topFundingPurpose: 'Working Capital & Expansion',
      businessGrowthRate: '4.7%'
    }
  };

  return economicData[city.slug] || economicData['new-york'];
};

// City-specific geographic coordinates for geo-tags
const getCityCoordinates = (citySlug: string): { lat: string; lng: string; region: string; placename: string } => {
  const coordinates: Record<string, { lat: string; lng: string; region: string; placename: string }> = {
    'new-york': { lat: '40.7128', lng: '-74.0060', region: 'US-NY', placename: 'New York' },
    'miami': { lat: '25.7617', lng: '-80.1918', region: 'US-FL', placename: 'Miami' },
    'dallas': { lat: '32.7767', lng: '-96.7970', region: 'US-TX', placename: 'Dallas' },
    'houston': { lat: '29.7604', lng: '-95.3698', region: 'US-TX', placename: 'Houston' },
    'los-angeles': { lat: '34.0522', lng: '-118.2437', region: 'US-CA', placename: 'Los Angeles' },
    'chicago': { lat: '41.8781', lng: '-87.6298', region: 'US-IL', placename: 'Chicago' },
    'atlanta': { lat: '33.7490', lng: '-84.3880', region: 'US-GA', placename: 'Atlanta' },
    'phoenix': { lat: '33.4484', lng: '-112.0740', region: 'US-AZ', placename: 'Phoenix' },
    'las-vegas': { lat: '36.1699', lng: '-115.1398', region: 'US-NV', placename: 'Las Vegas' },
    'tampa': { lat: '27.9506', lng: '-82.4572', region: 'US-FL', placename: 'Tampa' }
  };
  return coordinates[citySlug] || coordinates['new-york'];
};

// Remove the getCityBlogPosts function entirely and use getArticlesByCity from articleData.ts instead

// What lenders look for - educational content
const LENDER_CRITERIA = [
  {
    title: 'Credit Profile',
    icon: 'ri-bank-card-2-line',
    description: 'Both personal and business credit scores are evaluated. Most lenders prefer a personal score of 650+ and look for clean credit history without recent bankruptcies or defaults.',
    details: [
      'Personal credit score (typically 600-700+ preferred)',
      'Business credit score if established',
      'Payment history on existing obligations',
      'Credit utilization ratio (under 30% ideal)',
      'Length of credit history'
    ],
    tip: 'Check your credit reports before applying and dispute any errors. Even small improvements can affect your rates.'
  },
  {
    title: 'Revenue & Cash Flow',
    icon: 'ri-line-chart-line',
    description: 'Lenders analyze your bank statements to understand revenue patterns, cash flow consistency, and your ability to repay. Consistent deposits are more important than occasional large amounts.',
    details: [
      'Monthly revenue (typically $10K-$30K+ minimum)',
      'Revenue consistency and growth trends',
      'Debt Service Coverage Ratio (DSCR)',
      'Profit margins and net income',
      'Seasonal patterns and explanations'
    ],
    tip: 'Maintain organized financial records and be prepared to explain any unusual deposits or withdrawals.'
  },
  {
    title: 'Time in Business',
    icon: 'ri-calendar-check-line',
    description: 'The longer your business has operated, the more options become available. Most traditional lenders require at least 2 years, while alternative lenders may work with 6+ months.',
    details: [
      '6-12 months: Limited options, higher rates',
      '1-2 years: More options become available',
      '2+ years: Access to traditional bank loans',
      '5+ years: Best rates and terms available',
      'Industry experience can help newer businesses'
    ],
    tip: 'If you\'re a newer business, focus on building strong financials and consider starting with smaller funding amounts.'
  },
  {
    title: 'Collateral & Assets',
    icon: 'ri-shield-check-line',
    description: 'Some funding types require collateral to secure the loan. This can include real estate, equipment, inventory, or accounts receivable. Personal guarantees are often required.',
    details: [
      'Real estate (commercial or personal)',
      'Equipment and machinery',
      'Inventory and accounts receivable',
      'Personal guarantee requirements',
      'UCC filings and existing liens'
    ],
    tip: 'Understand what assets you\'re willing to pledge and how existing liens might affect your options.'
  },
  {
    title: 'Industry & Business Type',
    icon: 'ri-building-4-line',
    description: 'Some industries are considered higher risk and may face more scrutiny or limited options. Understanding your industry\'s risk profile helps set realistic expectations.',
    details: [
      'Lower risk: Healthcare, professional services',
      'Moderate risk: Retail, manufacturing',
      'Higher risk: Restaurants, construction',
      'Restricted: Cannabis, gambling, adult entertainment',
      'Seasonal businesses may need extra documentation'
    ],
    tip: 'Be prepared to explain your business model and how you manage industry-specific risks.'
  },
  {
    title: 'Use of Funds',
    icon: 'ri-funds-line',
    description: 'Lenders want to understand how you\'ll use the funding and how it will help your business grow or operate more efficiently. Clear purpose often leads to better terms.',
    details: [
      'Working capital and cash flow management',
      'Equipment purchase or upgrade',
      'Business expansion or new location',
      'Inventory purchase for growth',
      'Debt consolidation or refinancing'
    ],
    tip: 'Have a clear plan for how you\'ll use the funds and be able to articulate the expected ROI.'
  }
];

export default function CityHub() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const { city } = useCityContext();
  const [activeCriteria, setActiveCriteria] = useState(0);
  const [visiblePosts, setVisiblePosts] = useState(3);

  // Get economic data early so it's available throughout the component
  const economicData = city ? getCityEconomicData(city) : null;
  
  // Get city-specific blog posts from articleData.ts
  const cityBlogPosts = city ? getArticlesByCity(city.slug) : [];
  
  // Get city-specific FAQs
  const faqs = city ? getCityFAQs(city.name) : [];

  // Get coordinates for schema
  const coordinates = city ? getCityCoordinates(city.slug) : null;

  useEffect(() => {
    if (city) {
      track('page_view', {
        page: 'city_hub',
        city: city.name,
        state: city.state,
      });
    }
  }, [city]);

  useEffect(() => {
    if (city && economicData && coordinates) {
      const currentUrl = window.location.origin + window.location.pathname;
      
      // Primary SEO - Title
      document.title = `Business Funding ${city.name} ${city.stateCode} | Small Business Loans & Capital Solutions`;
      
      // Meta Description - Optimized for local SEO
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', `Get business funding in ${city.name}, ${city.state}. Explore working capital, term loans, equipment financing & more. Learn what ${city.name} lenders look for. Average loan: ${economicData.avgLoanAmount}. Educational guide for local businesses.`);

      // Meta Keywords - City-specific
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', `business funding ${city.name}, small business loans ${city.name}, ${city.name} business capital, working capital ${city.name} ${city.stateCode}, business loans ${city.state}, ${city.name} business financing, equipment financing ${city.name}, commercial loans ${city.name}, business credit ${city.name}, ${city.name} lenders`);

      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', currentUrl);

      // Geo-location tags for local SEO (ISO 3166 standard)
      let geoPosition = document.querySelector('meta[name="geo.position"]');
      if (!geoPosition) {
        geoPosition = document.createElement('meta');
        geoPosition.setAttribute('name', 'geo.position');
        document.head.appendChild(geoPosition);
      }
      geoPosition.setAttribute('content', `${coordinates.lat};${coordinates.lng}`);

      let geoPlacename = document.querySelector('meta[name="geo.placename"]');
      if (!geoPlacename) {
        geoPlacename = document.createElement('meta');
        geoPlacename.setAttribute('name', 'geo.placename');
        document.head.appendChild(geoPlacename);
      }
      geoPlacename.setAttribute('content', coordinates.placename);

      let geoRegion = document.querySelector('meta[name="geo.region"]');
      if (!geoRegion) {
        geoRegion = document.createElement('meta');
        geoRegion.setAttribute('name', 'geo.region');
        document.head.appendChild(geoRegion);
      }
      geoRegion.setAttribute('content', coordinates.region);

      let icbm = document.querySelector('meta[name="ICBM"]');
      if (!icbm) {
        icbm = document.createElement('meta');
        icbm.setAttribute('name', 'ICBM');
        document.head.appendChild(icbm);
      }
      icbm.setAttribute('content', `${coordinates.lat}, ${coordinates.lng}`);

      // Open Graph tags for social sharing
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', `Business Funding in ${city.name}, ${city.stateCode} | Complete Guide`);

      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', `Complete guide to business funding options in ${city.name}. Learn what lenders look for, explore funding types, and understand ${city.name}'s business economy.`);

      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute('content', currentUrl);

      let ogType = document.querySelector('meta[property="og:type"]');
      if (!ogType) {
        ogType = document.createElement('meta');
        ogType.setAttribute('property', 'og:type');
        document.head.appendChild(ogType);
      }
      ogType.setAttribute('content', 'website');

      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7Bcity.name%7D%20city%20skyline%20business%20district&width=1200&height=630&seq=og${city.slug}&orientation=landscape`);

      // Twitter Card tags
      let twitterCard = document.querySelector('meta[name="twitter:card"]');
      if (!twitterCard) {
        twitterCard = document.createElement('meta');
        twitterCard.setAttribute('name', 'twitter:card');
        document.head.appendChild(twitterCard);
      }
      twitterCard.setAttribute('content', 'summary_large_image');

      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', `Business Funding in ${city.name}, ${city.stateCode}`);

      let twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute('content', `Complete guide to business funding options in ${city.name}. Educational resources for local business owners.`);

      // Last Modified Date
      let lastModified = document.querySelector('meta[http-equiv="last-modified"]');
      if (!lastModified) {
        lastModified = document.createElement('meta');
        lastModified.setAttribute('http-equiv', 'last-modified');
        document.head.appendChild(lastModified);
      }
      lastModified.setAttribute('content', new Date().toUTCString());

      // Generate combined LocalBusiness + FAQ Schema using utility
      const structuredData = generateCityPageSchema({
        cityName: city.name,
        stateCode: city.stateCode,
        stateName: city.state,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        url: currentUrl,
        faqs: faqs,
        description: `Complete guide to business funding options in ${city.name}. Learn what lenders look for, explore funding types, and understand ${city.name}'s business economy. Average loan: ${economicData.avgLoanAmount}.`,
        avgLoanAmount: economicData.avgLoanAmount,
        keyIndustries: economicData.keyIndustries
      });

      // Remove existing schema script and add new one
      const existingSchema = document.querySelector('script[data-schema="city-page"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'city-page');
      schemaScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(schemaScript);

      // Cleanup function
      return () => {
        // Clean up meta tags when component unmounts
        const tagsToRemove = [
          'meta[name="geo.position"]',
          'meta[name="geo.placename"]',
          'meta[name="geo.region"]',
          'meta[name="ICBM"]',
          'meta[property="og:title"]',
          'meta[property="og:description"]',
          'meta[property="og:url"]',
          'meta[property="og:type"]',
          'meta[property="og:image"]',
          'meta[name="twitter:card"]',
          'meta[name="twitter:title"]',
          'meta[name="twitter:description"]'
        ];
        
        tagsToRemove.forEach(selector => {
          const element = document.querySelector(selector);
          if (element) {
            element.remove();
          }
        });

        // Remove schema script
        const schemaToRemove = document.querySelector('script[data-schema="city-page"]');
        if (schemaToRemove) {
          schemaToRemove.remove();
        }
      };
    }
  }, [city, economicData, coordinates, faqs]);

  if (!citySlug || !isValidCitySlug(citySlug)) {
    return <Navigate to="/business-funding" replace />;
  }

  if (!city || !economicData) {
    return <Navigate to="/business-funding" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-[#C22F3B] transition-colors cursor-pointer">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <Link to="/business-funding" className="text-gray-600 hover:text-[#C22F3B] transition-colors cursor-pointer">
              Business Funding
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400" />
            <span className="text-gray-900 font-semibold">{city.name}, {city.stateCode}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={`https://readdy.ai/api/search-image?query=$%7Bcity.name%7D%20city%20skyline%20business%20district%20modern%20architecture%20professional%20urban%20landscape%20daytime%20clear%20sky%20vibrant%20economy&width=1920&height=1080&seq=cityhero${city.slug}&orientation=landscape`}
            alt={`${city.name} business district`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-sm font-medium text-white mb-6">
              <i className="ri-map-pin-line text-amber-400"></i>
              <span>{city.state} • {city.region} Region</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Business Funding in
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                {city.name}, {city.stateCode}
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              Your complete guide to understanding business funding options in {city.name}. 
              Learn what lenders look for, explore funding types, and discover how {city.name}'s 
              thriving economy creates opportunities for business growth.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to={`/cities/${city.slug}/prequalification`}
                onClick={() => track('cta_clicked', { cta: 'hero_prequalification', city: city.name })}
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Check Your Options
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <a
                href="#what-lenders-look-for"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-2"></i>
                Learn First
              </a>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-2xl font-bold text-amber-400">{economicData.avgLoanAmount}</div>
                <div className="text-sm text-gray-400">Avg. Loan Amount</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-2xl font-bold text-amber-400">{economicData.businessGrowthRate}</div>
                <div className="text-sm text-gray-400">Business Growth</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                <div className="text-2xl font-bold text-amber-400">{city.businessCount}</div>
                <div className="text-sm text-gray-400">Local Businesses</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Notice */}
      <section className="bg-amber-50 border-y border-amber-200 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-amber-700 text-xl" />
            </div>
            <div className="text-sm text-amber-900">
              <strong>Educational Platform.</strong> We provide information to help {city.name} business owners understand funding options. 
              We are not a lender. Approval and terms depend on individual circumstances and lender requirements.
            </div>
          </div>
        </div>
      </section>

      {/* City Economic Overview */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Local Economy
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {city.name}'s Business Landscape
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {economicData.businessClimate}
              </p>
              
              <div className="space-y-4 mb-8">
                {economicData.economicHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-emerald-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{highlight}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-3">Top Funding Purpose in {city.name}</h4>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className="ri-funds-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{economicData.topFundingPurpose}</div>
                    <div className="text-sm text-gray-500">Most common use of business funding</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Key Industries in {city.name}</h3>
              <div className="grid grid-cols-2 gap-4">
                {economicData.keyIndustries.map((industry, index) => (
                  <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/10">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-lg mb-3">
                      <i className={`ri-${
                        industry.includes('Financial') ? 'bank' :
                        industry.includes('Technology') ? 'code-s-slash' :
                        industry.includes('Healthcare') ? 'heart-pulse' :
                        industry.includes('Real Estate') ? 'building-2' :
                        industry.includes('Entertainment') ? 'film' :
                        industry.includes('Tourism') ? 'plane' :
                        industry.includes('Energy') ? 'flashlight' :
                        industry.includes('Manufacturing') ? 'tools' :
                        industry.includes('Trade') ? 'ship' :
                        'briefcase'
                      }-line text-amber-400`}></i>
                    </div>
                    <div className="text-white font-medium text-sm">{industry}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Lenders Look For - Interactive Section */}
      <section id="what-lenders-look-for" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 text-sm font-semibold rounded-full mb-4">
              Educational Guide
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Lenders Look For in {city.name}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding these key factors helps you prepare for the funding process and set realistic expectations. 
              Each lender weighs these criteria differently.
            </p>
          </div>

          {/* Criteria Tabs */}
          <div className="bg-white rounded-xl p-2 mb-8 max-w-4xl mx-auto shadow-sm border border-gray-200">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {LENDER_CRITERIA.map((criteria, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCriteria(index)}
                  className={`px-3 py-3 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    activeCriteria === index
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${criteria.icon} block text-lg mb-1`}></i>
                  <span className="hidden md:block">{criteria.title.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Criteria Content */}
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-10">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl mb-6">
                    <i className={`${LENDER_CRITERIA[activeCriteria].icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {LENDER_CRITERIA[activeCriteria].title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {LENDER_CRITERIA[activeCriteria].description}
                  </p>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-start gap-2">
                      <i className="ri-lightbulb-line text-amber-600 mt-0.5"></i>
                      <div>
                        <div className="font-semibold text-amber-900 text-sm mb-1">Pro Tip</div>
                        <p className="text-sm text-amber-800">{LENDER_CRITERIA[activeCriteria].tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 md:p-10">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    What Lenders Evaluate
                  </h4>
                  <ul className="space-y-4">
                    {LENDER_CRITERIA[activeCriteria].details.map((detail, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center bg-white rounded-full flex-shrink-0 mt-0.5 shadow-sm">
                          <span className="text-xs font-bold text-amber-600">{index + 1}</span>
                        </div>
                        <span className="text-gray-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Funding Types for City */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-4">
              Funding Options
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Funding Types Available in {city.name}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore detailed information about each funding type. Understanding your options helps you choose 
              the right solution for your {city.name} business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FUNDING_TYPES.map((fundingType) => {
              let fundingRoute = '/business-funding';
              if (fundingType.slug === 'working-capital') {
                fundingRoute = '/funding/working-capital';
              } else if (fundingType.slug === 'line-of-credit') {
                fundingRoute = '/funding/line-of-credit';
              } else if (fundingType.slug === 'term-loans') {
                fundingRoute = '/funding/term-loans';
              } else if (fundingType.slug === 'equipment-financing') {
                fundingRoute = '/funding/equipment-financing';
              } else if (fundingType.slug === 'commercial-real-estate') {
                fundingRoute = '/funding/commercial-real-estate';
              }

              return (
                <Link
                  key={fundingType.slug}
                  to={fundingRoute}
                  onClick={() => track('funding_type_selected', { city: city.name, funding_type: fundingType.name })}
                  className="group bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl hover:border-transparent transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg mb-4 group-hover:bg-amber-100 transition-colors">
                    <i className={`${fundingType.icon} text-gray-600 group-hover:text-amber-600 text-xl transition-colors`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    {fundingType.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {fundingType.shortDescription}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Amount</span>
                      <span className="font-semibold text-gray-900">{fundingType.range}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Timeline</span>
                      <span className="font-semibold text-gray-900">{fundingType.timeline}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Term</span>
                      <span className="font-semibold text-gray-900">{fundingType.typicalTerm}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm">
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4">
              Resources & Insights
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Latest Articles for {city.name} Business Owners
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Up to-date insights on business funding trends, industry developments, 
              and financial strategies specific to {city.name}'s key industries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cityBlogPosts.slice(0, visiblePosts).map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all group"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <i className="ri-calendar-line" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/cities/${city.slug}/articles/${post.slug}`}
                    className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm hover:gap-3 transition-all cursor-pointer"
                  >
                    Read Article
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {visiblePosts < cityBlogPosts.length && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisiblePosts(cityBlogPosts.length)}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg border-2 border-gray-200 hover:border-amber-500 hover:text-amber-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Load More Articles
                <i className="ri-arrow-down-line ml-2" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
              Common Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Answers to common questions from {city.name} business owners about funding
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden group"
              >
                <summary className="px-6 py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                  <i className="ri-add-line text-2xl text-amber-500 group-open:rotate-45 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Explore Your Options in {city.name}?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Complete our quick pre-qualification form to see what funding options may be available 
            for your {city.name} business. No credit impact, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/cities/${city.slug}/prequalification`}
              onClick={() => track('cta_clicked', { cta: 'bottom_prequalification', city: city.name })}
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
            >
              Start Pre-Qualification
              <i className="ri-arrow-right-line ml-2" />
            </Link>
            <Link
              to="/business-funding"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line mr-2"></i>
              View All Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
